import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentServingOrder } from '@/store/service';
import { ImmersionValueContext } from '../index';
import { getGradientColor, hexToRgb } from './utils';

import cx from './index.module.scss';

interface Props {
  massageHeadRealtimeStatus: MassageHeadRealtimeStatus;
}

const MassageHeadUI: React.FC<Props> = ({ massageHeadRealtimeStatus }) => {
  const massageItem = useSelector(selectCurrentServingOrder)?.productItems?.[0];
  const { robotState, massageState } = useContext(ImmersionValueContext);

  const isPaused = useMemo(() => ['Pausing', 'Paused'].includes(massageState), [massageState]);

  const isTouchBody = useMemo(
    () => massageHeadRealtimeStatus?.human_touched && massageState === 'Running_Massaging',
    [massageHeadRealtimeStatus?.human_touched, massageState]
  );

  // 按摩方案的宽度
  const massageInnerWidth = useMemo(
    () => (massageHeadRealtimeStatus?.actual_force / 100) * 50,
    [massageHeadRealtimeStatus?.actual_force]
  );
  // 磁震波方案的宽度
  const swInnerWidth = useMemo(() => (robotState?.force / 100) * 42, [robotState?.force]);
  // 射频/热砭石方案的背景颜色
  const rfRadiusRgb = useMemo(
    () =>
      hexToRgb(
        getGradientColor(
          '#FFF38F',
          '#FF6C52',
          massageItem === 'RadioFrequency' ? robotState?.rfPower : robotState?.hotStonePower
        )
      ),
    [massageItem, robotState]
  );
  // 艾灸方案的背景颜色
  const moxRadiusRgb = useMemo(
    // 机器人机械臂高度为 80-200（mm）
    () => hexToRgb(getGradientColor('#FF6C52', '#FFF38F', ((robotState?.height - 80) / (200 - 80)) * 100)),
    [robotState?.height]
  );

  return (
    <div className={cx('MassageHeadUI')}>
      {massageItem === 'Massage' ? (
        <div className={cx('head-wrapper', { show: !isPaused })}>
          <div className={cx('massage', { show: isTouchBody })}>
            <div
              className={cx('massage-inner')}
              style={{ width: `${massageInnerWidth}px`, height: `${massageInnerWidth}px` }}
            />
          </div>
          <div className={cx('no-touch-body', { show: !isTouchBody })} />
        </div>
      ) : massageItem === 'Shockwave' ? (
        <div className={cx('head-wrapper', { show: !isPaused })}>
          <div className={cx('shockwave', { show: isTouchBody })}>
            <div
              className={cx('shockwave-inner')}
              style={{ width: `${swInnerWidth}px`, height: `${swInnerWidth}px` }}
            />
            <div className={cx('sw-inner')} />
            <div className={cx('sw-inner')} />
            <div className={cx('sw-inner')} />
            <div className={cx('sw-inner')} />
            <div className={cx('sw-inner')} />
          </div>

          <div className={cx('no-touch-body', { show: !isTouchBody })} />
        </div>
      ) : ['HotStone', 'RadioFrequency'].includes(massageItem) ? (
        <div className={cx('head-wrapper', { show: !isPaused })}>
          <div
            className={cx('rf', { show: isTouchBody })}
            style={{ '--radius-color': rfRadiusRgb } as React.CSSProperties}
          >
            <div className={cx('rf-inner', 'wave-inner-1')} />
            <div className={cx('rf-inner', 'wave-inner-2')} />
            <div className={cx('rf-inner', 'wave-inner-3')} />
            <div className={cx('rf-inner', 'wave-inner-4')} />
            <div className={cx('rf-inner', 'wave-inner-5')} />
            <div className={cx('rf-inner', 'wave-inner-6')} />
          </div>
          <div className={cx('no-touch-body', { show: !isTouchBody })} />
        </div>
      ) : massageItem === 'Moxibustion' ? (
        <div className={cx('head-wrapper', { show: !isPaused })}>
          <div
            className={cx('mox', { show: isTouchBody })}
            style={{ '--radius-color': moxRadiusRgb } as React.CSSProperties}
          >
            <div className={cx('mox-inner', 'wave-inner-1')} />
            <div className={cx('mox-inner', 'wave-inner-2')} />
            <div className={cx('mox-inner', 'wave-inner-3')} />
            <div className={cx('mox-inner', 'wave-inner-4')} />
            <div className={cx('mox-inner', 'wave-inner-5')} />
            <div className={cx('mox-inner', 'wave-inner-6')} />
            <div className={cx('inner-bg-rect')} style={{ 'inner-bg-color': moxRadiusRgb } as React.CSSProperties} />
          </div>
          <div className={cx('no-touch-body', { show: !isTouchBody })} />
        </div>
      ) : null}
    </div>
  );
};

export default MassageHeadUI;
